import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import { del, get, post } from "../../lib/request"

export const AD_PLATFORM_SOCIAL_TYPE_ID = {
  facebook: 1,
  linkedin: 6,
}

const EMPTY_AD_ACCOUNT_CONNECTION = {
  connected: false,
  accountName: "",
  authenticationId: "",
  clientAdAccountId: null,
}

export function emptyAdAccountConnections() {
  return {
    facebook: { ...EMPTY_AD_ACCOUNT_CONNECTION },
    linkedin: { ...EMPTY_AD_ACCOUNT_CONNECTION },
  }
}

export function adPlatformIdFromSocialNetworkTypeId(socialNetworkTypeId) {
  const id = Number(socialNetworkTypeId)
  return Object.entries(AD_PLATFORM_SOCIAL_TYPE_ID).find(([, typeId]) => typeId === id)?.[0] ?? null
}

export function adPlatformIdFromDescription(description) {
  const key = String(description ?? "").toLowerCase()
  if (key === "facebook") return "facebook"
  if (key === "linkedin") return "linkedin"
  return null
}

export function buildAdAccountNotFoundError(adAccountNo, platformId) {
  return `Couldn't find any AdAccount for the AdAccountNo \`${adAccountNo}\` and social network type \`${platformId}\` on the system. Only pre-existent accounts are allowed to be added.`
}

export function extractClientAdAccountsList(payload) {
  if (payload == null) return []
  if (Array.isArray(payload)) return payload
  if (Array.isArray(payload.data)) return payload.data
  if (Array.isArray(payload.content)) return payload.content
  if (Array.isArray(payload.results)) return payload.results
  return []
}

/** Client-ad-account link id used for DELETE /client-ad-accounts/{id}. */
export function resolveClientAdAccountId(data) {
  if (data == null || typeof data !== "object") return null
  const id = data.id ?? data.clientAdAccountId ?? data.client_ad_account_id
  return id != null && id !== "" ? Number(id) : null
}

/**
 * Normalizes GET /client-ad-accounts/clients/{clientId} rows:
 * { id, clientId, adAccount: { adAccountNo, socialNetworkType } }
 */
export function normalizeClientAdAccountRow(raw) {
  if (!raw || typeof raw !== "object") {
    return { ...EMPTY_AD_ACCOUNT_CONNECTION, platformId: null }
  }

  const adAccount = raw.adAccount ?? raw.ad_account ?? null
  const socialNetworkType =
    adAccount?.socialNetworkType ??
    adAccount?.social_network_type ??
    raw.socialNetworkType ??
    raw.social_network_type ??
    null

  const socialNetworkTypeId =
    socialNetworkType?.id ?? raw.socialNetworkTypeId ?? raw.social_network_type_id

  const platformId =
    adPlatformIdFromSocialNetworkTypeId(socialNetworkTypeId) ??
    adPlatformIdFromDescription(socialNetworkType?.description)

  const adAccountNo =
    adAccount?.adAccountNo ??
    adAccount?.ad_account_no ??
    raw.adAccountNo ??
    raw.ad_account_no ??
    ""

  const clientAdAccountId = resolveClientAdAccountId(raw)
  const authenticationId = String(adAccountNo)

  return {
    clientAdAccountId,
    platformId,
    connected: Boolean(clientAdAccountId && platformId),
    accountName: authenticationId,
    authenticationId,
  }
}

export function mapClientAdAccountsToConnections(accounts) {
  const next = emptyAdAccountConnections()

  for (const raw of extractClientAdAccountsList(accounts)) {
    const row = normalizeClientAdAccountRow(raw)
    if (row.platformId && next[row.platformId]) {
      next[row.platformId] = {
        connected: row.connected,
        accountName: row.accountName,
        authenticationId: row.authenticationId,
        clientAdAccountId: row.clientAdAccountId,
      }
    }
  }

  return next
}

/**
 * GET /api/v1/client-ad-accounts/clients/{clientId}
 */
export const fetchClientAdAccounts = createAsyncThunk(
  "clientAdAccount/fetch",
  async (clientId, { rejectWithValue }) => {
    try {
      if (!clientId) throw new Error("clientId is required")
      const data = await get(`client-ad-accounts/clients/${clientId}`)
      return {
        clientId,
        accounts: extractClientAdAccountsList(data),
      }
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to fetch ad accounts")
    }
  },
)

/**
 * POST /api/v1/client-ad-accounts/clients/{clientId}
 * Body: { socialNetworkTypeId, adAccountNo }
 */
export const connectClientAdAccount = createAsyncThunk(
  "clientAdAccount/connect",
  async ({ clientId, adAccountNo, platformId }, { rejectWithValue }) => {
    try {
      if (!clientId) throw new Error("clientId is required")
      if (!adAccountNo?.trim()) throw new Error("Authentication ID is required")

      const socialNetworkTypeId = AD_PLATFORM_SOCIAL_TYPE_ID[platformId]
      if (!socialNetworkTypeId) throw new Error("Unsupported ad account platform")

      const data = await post(`client-ad-accounts/clients/${clientId}`, {
        socialNetworkTypeId,
        adAccountNo: String(adAccountNo).trim(),
      })
      return {
        clientId,
        platformId,
        adAccountNo: String(adAccountNo).trim(),
        clientAdAccountId: resolveClientAdAccountId(data),
        data,
      }
    } catch (err) {
      const message =
        (err && typeof err === "object" && err.message && String(err.message)) ||
        buildAdAccountNotFoundError(String(adAccountNo ?? "").trim(), platformId)
      return rejectWithValue(message)
    }
  },
)

/**
 * DELETE /api/v1/client-ad-accounts/{clientAdAccountId}
 */
export const deleteClientAdAccount = createAsyncThunk(
  "clientAdAccount/delete",
  async ({ clientAdAccountId }, { rejectWithValue }) => {
    try {
      if (!clientAdAccountId) throw new Error("clientAdAccountId is required")
      await del(`client-ad-accounts/${clientAdAccountId}`)
      return { clientAdAccountId: Number(clientAdAccountId) }
    } catch (err) {
      return rejectWithValue(err?.message || "Failed to delete ad account")
    }
  },
)

const initialState = {
  connectStatus: "idle",
  connectError: null,
  lastConnected: null,
  fetchStatus: "idle",
  fetchError: null,
  accountsByClient: {},
  deleteStatus: "idle",
  deleteError: null,
}

const clientAdAccountSlice = createSlice({
  name: "clientAdAccount",
  initialState,
  reducers: {
    clearClientAdAccountConnectError(state) {
      state.connectError = null
    },
    resetClientAdAccountConnect(state) {
      state.connectStatus = "idle"
      state.connectError = null
    },
    resetClientAdAccountDelete(state) {
      state.deleteStatus = "idle"
      state.deleteError = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchClientAdAccounts.pending, (state) => {
        state.fetchStatus = "loading"
        state.fetchError = null
      })
      .addCase(fetchClientAdAccounts.fulfilled, (state, action) => {
        state.fetchStatus = "succeeded"
        state.fetchError = null
        const { clientId, accounts } = action.payload
        state.accountsByClient[String(clientId)] = accounts
      })
      .addCase(fetchClientAdAccounts.rejected, (state, action) => {
        state.fetchStatus = "failed"
        state.fetchError = action.payload || "Failed to fetch ad accounts"
      })
      .addCase(connectClientAdAccount.pending, (state) => {
        state.connectStatus = "loading"
        state.connectError = null
      })
      .addCase(connectClientAdAccount.fulfilled, (state, action) => {
        state.connectStatus = "succeeded"
        state.connectError = null
        state.lastConnected = action.payload
        const { clientId, data } = action.payload
        if (!clientId || !data) return
        const key = String(clientId)
        const current = Array.isArray(state.accountsByClient[key]) ? state.accountsByClient[key] : []
        const nextId = resolveClientAdAccountId(data)
        const withoutDuplicate = current.filter((row) => resolveClientAdAccountId(row) !== nextId)
        state.accountsByClient[key] = [...withoutDuplicate, data]
      })
      .addCase(connectClientAdAccount.rejected, (state, action) => {
        state.connectStatus = "failed"
        state.connectError = action.payload || "Failed to connect ad account"
      })
      .addCase(deleteClientAdAccount.pending, (state) => {
        state.deleteStatus = "loading"
        state.deleteError = null
      })
      .addCase(deleteClientAdAccount.fulfilled, (state, action) => {
        state.deleteStatus = "succeeded"
        state.deleteError = null
        const deletedId = action.payload.clientAdAccountId
        for (const clientKey of Object.keys(state.accountsByClient)) {
          state.accountsByClient[clientKey] = state.accountsByClient[clientKey].filter(
            (row) => resolveClientAdAccountId(row) !== deletedId,
          )
        }
      })
      .addCase(deleteClientAdAccount.rejected, (state, action) => {
        state.deleteStatus = "failed"
        state.deleteError = action.payload || "Failed to delete ad account"
      })
  },
})

export const {
  clearClientAdAccountConnectError,
  resetClientAdAccountConnect,
  resetClientAdAccountDelete,
} = clientAdAccountSlice.actions

export default clientAdAccountSlice.reducer
